<div class="parking-type-space-container">
    <section class="bg-white border border-slate-100 rounded-xl shadow-sm hover:shadow-md transition-shadow p-4">
        <!-- Header — Administrator only: the parking-types catalog itself is hidden from operator admins -->
        @if (isAdmin()) {
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div class="flex items-center gap-2">
                <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 text-blue-600">local_parking</mat-icon>
                <h3 class="text-sm font-semibold text-slate-900">Parking Types</h3>
                <span class="text-xs font-medium text-slate-400">({{ totalCount() }})</span>
            </div>

            <div class="flex items-center gap-2">
                <button type="button" (click)="refresh()" [disabled]="isLoading()" matTooltip="Refresh"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors disabled:opacity-50">
                    <mat-icon class="!w-5 !h-5 !text-[20px] leading-5"
                        [class.animate-spin]="isLoading()">refresh</mat-icon>
                </button>
                <button type="button" (click)="openAddParkingTypeDialog()"
                    class="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors px-3 py-1.5 rounded-lg">
                    <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">add</mat-icon>
                    Add Type
                </button>
            </div>
        </div>
        }

        <!-- Loading State -->
        @if (isLoading()) {
        <div class="flex items-center justify-center py-8">
            <mat-spinner diameter="36"></mat-spinner>
        </div>
        } @else {
        <!-- Parking Types List -->
        @if (allParkingTypes().length > 0 || easypassEnabled() || easypassTypes().length > 0) {
        @if (isAdmin() && allParkingTypes().length > 0) {
        <div class="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
            @for (parkingTypeItem of pagedParkingTypes(); track parkingTypeItem.id) {
            <app-parking-type-item [parkingTypeItem]="parkingTypeItem" [parkingSpaceId]="parkingSpace.id!"
                [typeCategory]="getTypeCategory(parkingTypeItem)" (deleted)="onDeleteType($event)">
            </app-parking-type-item>
            }
        </div>
        }

        <!-- Easypass invitations: collapsed row + invitations sublist — visible to operator admins too -->
        @if (easypassEnabled() || easypassTypes().length > 0) {
        <div class="mt-3 border border-slate-100 rounded-lg overflow-hidden">
            <div class="flex items-center justify-between gap-3 p-3 bg-white">
                <div class="flex items-center gap-3 min-w-0">
                    <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 text-amber-500">qr_code_2</mat-icon>
                    <div class="min-w-0">
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-semibold text-slate-900">Easypass invitations</span>
                        </div>
                        <div class="text-xs text-slate-500">
                            {{ easypassCount() }} invitation{{ easypassCount() === 1 ? '' : 's' }}
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-2">
                    @if (canIssueEasypass()) {
                    <button type="button" (click)="openIssueEasypassDialog()"
                        class="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors px-3 py-1.5 rounded-lg">
                        <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">qr_code_2</mat-icon>
                        Create invitation
                    </button>
                    }
                    @if (easypassCount() > 0) {
                    <button type="button" (click)="toggleIssuedEasypasses()"
                        class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                        [matTooltip]="showIssuedEasypasses() ? 'Hide invitations' : 'Show invitations'">
                        <mat-icon class="!w-5 !h-5 !text-[20px] leading-5">
                            {{ showIssuedEasypasses() ? 'expand_less' : 'expand_more' }}
                        </mat-icon>
                    </button>
                    }
                </div>
            </div>

            @if (showIssuedEasypasses() && easypassCount() > 0) {
            <div class="flex items-center gap-2 p-3 border-t border-slate-100 bg-white">
                <input type="search" [value]="easypassSearch()"
                    (input)="onEasypassSearchInput($any($event.target).value)"
                    placeholder="Search name or license plate…"
                    class="w-full sm:w-72 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition" />
                @if (easypassLoading()) {
                <mat-spinner diameter="18"></mat-spinner>
                }
            </div>
            @if (!easypassLoading() && easypassTypes().length === 0) {
            <p class="text-xs text-slate-500 text-center py-4 border-t border-slate-100">No invitation matches.</p>
            }
            <div class="divide-y divide-slate-100 border-t border-slate-100">
                @for (pass of easypassTypes(); track pass.id) {
                <div class="flex items-center justify-between gap-3 p-3 bg-slate-50">
                    <div class="min-w-0">
                        <div class="flex items-center gap-2 flex-wrap">
                            <span class="text-sm font-medium text-slate-800">{{ pass.title || easypassTokenShort(pass)
                                }}</span>
                            @if (pass.isConsumed) {
                            <jjames-badge variant="secondary">full</jjames-badge>
                            } @else {
                            <jjames-badge variant="success">open</jjames-badge>
                            }
                            <span
                                class="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-200 rounded px-1.5 py-0.5">
                                <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-4">directions_car</mat-icon>
                                @if (pass.isUnlimited) {
                                {{ 'easypassList.redeemedUnlimited' | translate: { count: easypassRedeemedCount(pass) } }}
                                } @else {
                                {{ 'easypassList.left' | translate: { left: easypassLeftCount(pass), max: pass.maxRedemptions ?? 0 } }}
                                }
                            </span>
                            <span
                                class="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-200 rounded px-1.5 py-0.5">
                                <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-4">logout</mat-icon>
                                {{ 'easypassList.stays' | translate: { count: pass.stayCount ?? 0 } }}
                            </span>
                            @if (isParkerPays(pass)) {
                            <span
                                class="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-100 rounded px-1.5 py-0.5">
                                <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-4">point_of_sale</mat-icon>Parker pays
                            </span>
                            } @else {
                            <span
                                class="inline-flex items-center gap-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded px-1.5 py-0.5">
                                <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-4">corporate_fare</mat-icon>Org pays
                            </span>
                            }
                        </div>
                        <div class="text-xs text-slate-500">
                            {{ easypassEntriesLabel(pass) }} · {{ easypassWindow(pass) }}
                        </div>
                        @if (redeemedPlate(pass); as plates) {
                        <div class="text-xs text-slate-600 mt-1 flex items-center gap-1">
                            <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-4">directions_car</mat-icon>
                            <span class="break-all">{{ plates }}</span>
                        </div>
                        }
                        @if (internalNote(pass); as note) {
                        <div class="text-xs text-slate-500 mt-1 flex items-start gap-1">
                            <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-4 mt-px">sticky_note_2</mat-icon>
                            <span class="break-all">{{ note }}</span>
                        </div>
                        }
                        @if (guestNote(pass); as guest) {
                        <div class="text-xs text-amber-700 mt-1 flex items-start gap-1">
                            <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-4 mt-px">campaign</mat-icon>
                            <span class="break-all italic">{{ guest }}</span>
                        </div>
                        }
                    </div>
                    <div class="flex items-center gap-1">
                        @if (pass.isExitTicket) {
                        <span class="text-xs text-slate-500 mr-1">
                            {{ 'easypassList.codesUsed' | translate: { used: pass.codesUsed ?? 0, total: pass.codeCount ?? 0 } }}
                        </span>
                        <button type="button" (click)="openExitTicketPrint(pass)" [matTooltip]="'easypassList.printCodes' | translate"
                            class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors">

                            <mat-icon class="!w-5 !h-5 !text-[18px] leading-5">print</mat-icon>

                        </button>
                        }
                        @if (!pass.isUnlimited && !pass.isExitTicket) {
                        <button type="button" (click)="onIncreaseCapacity(pass)" matTooltip="Increase capacity"
                            class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
                            <mat-icon class="!w-5 !h-5 !text-[18px] leading-5">add_circle_outline</mat-icon>
                        </button>
                        }
                        <!-- An exit-ticket pass's shared link is refused; only its printed codes redeem. -->
                        @if (!pass.isExitTicket) {
                        <a [href]="easypassRedeemUrl(pass)" target="_blank" rel="noopener"
                            matTooltip="Redeem in parker app"
                            class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700 transition-colors">
                            <mat-icon class="!w-5 !h-5 !text-[18px] leading-5">open_in_new</mat-icon>
                        </a>
                        }
                        @if (!pass.isExitTicket) {
                        <button type="button" (click)="togglePassQr(pass.id ?? '')" matTooltip="Show QR Code"
                            class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-violet-500 hover:bg-violet-50 hover:text-violet-700 transition-colors">
                            <mat-icon class="!w-5 !h-5 !text-[18px] leading-5">qr_code</mat-icon>
                        </button>
                        }
                        <button type="button" (click)="togglePassStays(pass)" matTooltip="Show stays"
                            class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-blue-500 hover:bg-blue-50 hover:text-blue-700 transition-colors">
                            <mat-icon class="!w-5 !h-5 !text-[18px] leading-5">history</mat-icon>
                        </button>
                        <button type="button" (click)="onDeleteEasypass(pass)" matTooltip="Revoke"
                            class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors">
                            <mat-icon class="!w-5 !h-5 !text-[18px] leading-5">delete_outline</mat-icon>
                        </button>
                    </div>
                </div>
                @if (qrVisiblePassId() === pass.id) {
                <div class="qr-panel border-t border-violet-200 bg-violet-50 px-3 py-3" [attr.data-pass-id]="pass.id">
                    <div class="flex flex-col items-center gap-2">
                        <qrcode [qrdata]="easypassRedeemUrl(pass)" [width]="200" [margin]="2" [colorDark]="'#000'"
                            [colorLight]="'#FFFFFF'" elementType="canvas" cssClass="rounded-lg shadow-sm"></qrcode>
                        <div class="flex items-center gap-2">
                            <button type="button" (click)="copyRedeemLink(pass)"
                                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-violet-200 transition-colors">
                                <mat-icon class="!w-4 !h-4 !text-[16px]">content_copy</mat-icon>
                                Copy Link
                            </button>
                            <button type="button" (click)="downloadPassQr(pass)"
                                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-violet-200 transition-colors">
                                <mat-icon class="!w-4 !h-4 !text-[16px]">download</mat-icon>
                                Download
                            </button>
                        </div>
                        <p class="text-xs  break-all text-center">{{ easypassRedeemUrl(pass) }}</p>
                    </div>
                </div>
                }
                @if (staysVisiblePassId() === pass.id) {
                <div class="border-t border-blue-200 bg-blue-50/60 px-3 py-3">
                    @if (staysLoading()) {
                    <p class="text-xs text-slate-500 text-center py-2">Loading stays…</p>
                    } @else if (staysFor(pass).length === 0) {
                    <p class="text-xs text-slate-500 text-center py-2">
                        No vehicle has parked with this invitation yet.
                    </p>
                    } @else {
                    <div class="overflow-x-auto">
                        <table class="w-full text-xs">
                            <thead>
                                <tr class="text-left text-slate-500 border-b border-blue-200">
                                    <th class="py-1.5 pr-3 font-semibold">Plate</th>
                                    <th class="py-1.5 pr-3 font-semibold">Entered</th>
                                    <th class="py-1.5 pr-3 font-semibold">Exited</th>
                                    <th class="py-1.5 font-semibold">Zone</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (stay of staysFor(pass); track stay.id) {
                                <tr class="border-b border-blue-100 last:border-0">
                                    <td class="py-1.5 pr-3 font-mono font-semibold text-slate-800">
                                        {{ stay.licensePlateDetails?.fullNumber || '—' }}
                                    </td>
                                    <td class="py-1.5 pr-3 text-slate-600 whitespace-nowrap">
                                        {{ stay.startedAt ? (stay.startedAt | date:'dd.MM.yyyy HH:mm') : '—' }}
                                    </td>
                                    <td class="py-1.5 pr-3 whitespace-nowrap">
                                        @if (stay.endedAt) {
                                        <span class="text-slate-600">{{ stay.endedAt | date:'dd.MM.yyyy HH:mm' }}</span>
                                        } @else {
                                        <span class="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                            Still inside
                                        </span>
                                        }
                                    </td>
                                    <td class="py-1.5 text-slate-600">{{ stay.parkingZoneDetails?.name || '—' }}</td>
                                </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                    }
                </div>
                }
                }
            </div>
            @if (easypassTotal() > 0) {
            <div class="px-3 py-2 border-t border-slate-100 bg-white">
                <jjames-paginator [config]="easypassPagination()" (pageChange)="onEasypassPageChange($event)" />
            </div>
            }
            }
        </div>
        }

        <!-- Paginator (client-side over the merged catalog) -->
        @if (isAdmin() && allParkingTypes().length > pageSize()) {
        <mat-paginator [length]="allParkingTypes().length" [pageSize]="pageSize()" [pageIndex]="pageIndex()"
            [pageSizeOptions]="[5, 10, 25]" [showFirstLastButtons]="!isHandset()" (page)="onPageChange($event)"
            class="mt-3">
        </mat-paginator>
        }
        } @else if (isAdmin()) {
        <!-- Empty State -->
        <div class="text-center py-8 bg-slate-50 border border-slate-100 rounded-lg">
            <mat-icon class="text-slate-300"
                style="font-size: 44px; width: 44px; height: 44px;">local_parking</mat-icon>
            <p class="text-sm text-slate-600 mt-2">No parking types assigned</p>
            <p class="text-xs text-slate-400 mt-1">Click "Add Type" to assign parking types to this space</p>
        </div>
        }
        }
    </section>
</div>